import { TwilioMessageChannel, TwilioPhoneNumberStatus } from '../../interfaces/twilio'
import { FilterFieldType, IFilter } from './types'

/**
 * Newest first. Not a column on the numbers table, so it is not in the config
 * and has to be allowed explicitly — see TWILIO_PHONE_NUMBERS_EXTRA_ORDER_BY.
 */
export const TWILIO_PHONE_NUMBERS_DEFAULT_SORT = 'created_at'

/**
 * orderBy values the numbers list accepts beyond its sortable columns. Passed to
 * buildFiltersValidationRules, which otherwise allows only what the config
 * names and rejects the default sort with NOT_ALLOWED_VALUE.
 */
export const TWILIO_PHONE_NUMBERS_EXTRA_ORDER_BY = [TWILIO_PHONE_NUMBERS_DEFAULT_SORT]

export const TWILIO_PHONE_NUMBERS_LIST_FILTERS_CONFIG = [
  {
    field: 'phoneNumber',
    dbField: 'phone_number',
    alias: 'number',
    type: FilterFieldType.Text,
    isSortable: true,
    title: 'Number',
  },
  {
    // Custom because a number's clients live on twilio_phone_number_assignment,
    // and a join would return a shared number once per client. ListPhoneNumbers
    // turns this into an EXISTS instead.
    field: 'clientName',
    type: FilterFieldType.Autocomplete,
    isCustom: true,
    isMultiple: true,
    title: 'Clients',
    placeholder: 'Client',
  },
  {
    // Which portal the client registered the number on. Free text on the
    // assignment, so it is custom for the same reason `clientName` is — and
    // because it is custom, the full-search box skips it; ListPhoneNumbers adds
    // it back explicitly.
    field: 'assignmentNotes',
    type: FilterFieldType.Text,
    isCustom: true,
    title: 'Portal',
  },
  {
    field: 'status',
    dbField: 'status',
    alias: 'number',
    type: FilterFieldType.List,
    isSortable: true,
    title: 'Status',
    options: [
      { value: TwilioPhoneNumberStatus.Unassigned, label: 'In pool' },
      { value: TwilioPhoneNumberStatus.Active, label: 'Active' },
      { value: TwilioPhoneNumberStatus.Unsupported, label: 'VoIP blocked' },
      { value: TwilioPhoneNumberStatus.Released, label: 'Released' },
    ],
  },
  {
    field: 'capabilities',
    type: FilterFieldType.Empty,
    title: 'Receives',
  },
  {
    field: 'action',
    type: FilterFieldType.Empty,
    title: 'Action',
    align: 'right',
  },
] as const satisfies IFilter[]

/** Newest first. A sortable column, so nothing extra is needed to allow it. */
export const TWILIO_MESSAGES_DEFAULT_SORT = 'received_at'

export const TWILIO_MESSAGES_LIST_FILTERS_CONFIG = [
  {
    field: 'receivedAt',
    dbField: 'received_at',
    alias: 'message',
    type: FilterFieldType.Date,
    isSortable: true,
    title: 'Received',
    // Date columns are right-aligned by default in the filters row; this one
    // renders as left-aligned text, so the header has to match it.
    align: 'left',
  },
  {
    // to_number rather than the joined number: it is always present, including
    // for a callback whose number Atlas does not track. A dropdown rather than
    // a text box so ops picks from the pool instead of retyping an E.164
    // number — not custom, so the free-search box still matches on it.
    field: 'phoneNumber',
    dbField: 'to_number',
    alias: 'message',
    type: FilterFieldType.Autocomplete,
    isMultiple: true,
    isSortable: true,
    title: 'Number',
    placeholder: 'Number',
  },
  {
    field: 'fromNumber',
    dbField: 'from_number',
    alias: 'message',
    type: FilterFieldType.Text,
    isSortable: true,
    title: 'From',
  },
  {
    field: 'body',
    dbField: 'body',
    alias: 'message',
    type: FilterFieldType.Text,
    isSortable: true,
    title: 'Message',
  },
  {
    field: 'channel',
    dbField: 'channel',
    alias: 'message',
    type: FilterFieldType.List,
    isSortable: true,
    title: 'Type',
    options: [
      { value: TwilioMessageChannel.Sms, label: 'SMS' },
      { value: TwilioMessageChannel.Voice, label: 'Voice' },
    ],
  },
  {
    // Custom for the same reason as the numbers list, plus the window: a
    // message belongs to whoever held the number when it arrived, so
    // ListMessages bounds the EXISTS by assigned_at/released_at.
    field: 'clientName',
    type: FilterFieldType.Autocomplete,
    isCustom: true,
    isMultiple: true,
    title: 'Clients',
    placeholder: 'Client',
  },
] as const satisfies IFilter[]
